import React, { useState, useEffect } from 'react';
import { api } from '../../api/client';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardHeader from '@mui/material/CardHeader';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import AdminLoading from './AdminLoading';
import AdminEmptyState from './AdminEmptyState';
import { IoPersonOutline, IoSearchOutline } from 'react-icons/io5';

function formatDate(str) {
  if (!str) return '–';
  try {
    return new Date(str).toLocaleDateString();
  } catch (_) {
    return str;
  }
}

function formatPaise(p) {
  return p != null ? `₹${(p / 100).toFixed(0)}` : '–';
}

export default function AdminCustomers() {
  const [list, setList] = useState([]);
  const [searchLeads, setSearchLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [detail, setDetail] = useState(null);
  const [search, setSearch] = useState('');

  const load = () => {
    Promise.all([api('/api/admin/customers'), api('/api/search-leads')])
      .then(([customers, leads]) => { setList(customers); setSearchLeads(leads); setError(''); })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => load(), []);

  const handleView = (id) => {
    api(`/api/admin/customers/${id}`).then(setDetail).catch((e) => setError(e.message));
  };

  const filteredList = search.trim()
    ? list.filter(
        (c) =>
          (c.email && c.email.toLowerCase().includes(search.toLowerCase())) ||
          (c.name && c.name.toLowerCase().includes(search.toLowerCase())) ||
          (c.phone && String(c.phone).includes(search))
      )
    : list;

  if (loading) return <AdminLoading />;

  return (
    <>
      <Card>
        <CardHeader title="Customers" subheader="Storefront registered customers" />
        <CardContent>
          {error && <Typography color="error" sx={{ mb: 2 }}>{error}</Typography>}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <TextField
              size="small"
              placeholder="Search by name, email, phone"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <IoSearchOutline size={20} color="action" />
                  </InputAdornment>
                ),
              }}
              sx={{ minWidth: 260 }}
            />
          </Box>
          {filteredList.length === 0 ? (
            <AdminEmptyState
              message={search ? 'No customers match your search.' : 'No customers yet. Customers register from the storefront.'}
              icon={IoPersonOutline}
            />
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: 'grey.100' }}>
                    <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Email</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Phone</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Joined</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>Orders</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredList.map((c) => (
                    <TableRow key={c.id} hover>
                      <TableCell>{c.name || '–'}</TableCell>
                      <TableCell>{c.email}</TableCell>
                      <TableCell>{c.phone || '–'}</TableCell>
                      <TableCell>{formatDate(c.created_at)}</TableCell>
                      <TableCell align="right">{c.order_count ?? 0}</TableCell>
                      <TableCell align="right">
                        <Button size="small" variant="outlined" onClick={() => handleView(c.id)}>
                          View
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      <Card sx={{ mt: 3 }}>
        <CardHeader title="Search leads" subheader="Named searches from the storefront" />
        <CardContent>
          {searchLeads.length === 0 ? (
            <AdminEmptyState message="No named searches yet." icon={IoSearchOutline} />
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: 'grey.100' }}>
                    <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Preferences</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Searched</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {searchLeads.map((lead) => {
                    const preferences = [
                      lead.gender,
                      lead.occasion,
                      lead.color,
                      lead.size,
                      lead.minPrice != null || lead.maxPrice != null
                        ? `₹${lead.minPrice ?? 0}–₹${lead.maxPrice ?? 'Any'}`
                        : null,
                    ].filter(Boolean).join(' · ');
                    return (
                      <TableRow key={lead.id} hover>
                        <TableCell>{lead.name}</TableCell>
                        <TableCell>{preferences || '–'}</TableCell>
                        <TableCell>{formatDate(lead.created_at)}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      <Dialog open={!!detail} onClose={() => setDetail(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Customer #{detail?.id}</DialogTitle>
        <DialogContent>
          {detail && (
            <Box sx={{ pt: 0 }}>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>Name:</strong> {detail.name || '–'}
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>Email:</strong> {detail.email}
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>Phone:</strong> {detail.phone || '–'}
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>Joined:</strong> {formatDate(detail.created_at)}
              </Typography>
              {detail.addresses && detail.addresses.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Addresses</Typography>
                  {detail.addresses.map((a) => (
                    <Typography key={a.id} variant="body2" sx={{ mb: 0.5 }}>
                      {a.label && `${a.label}: `}
                      {[a.line1, a.line2, a.city, a.state, a.pincode].filter(Boolean).join(', ')}
                      {a.is_default && ' (default)'}
                    </Typography>
                  ))}
                </Box>
              )}
              {detail.orders && detail.orders.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Orders ({detail.orders.length})</Typography>
                  <Box component="ul" sx={{ margin: 0, paddingLeft: 2 }}>
                    {detail.orders.slice(0, 10).map((o) => (
                      <li key={o.id}>
                        #{o.id} – {o.status} – {formatPaise(o.total_paise)} – {formatDate(o.created_at)}
                      </li>
                    ))}
                    {detail.orders.length > 10 && <li>… and {detail.orders.length - 10} more</li>}
                  </Box>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
